/**
 * PortfolioMap — executive-grade delivery map for the Portfolio dashboard.
 * Uses react-leaflet + Stadia Alidade Smooth (premium, high-contrast basemap)
 * with animated pulse rings on Red/Amber risk projects for instant scanning.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, useMap, LayersControl } from 'react-leaflet';
import { motion } from 'framer-motion';
import 'leaflet/dist/leaflet.css';

const RAG_COLORS = {
  red:   { fill: '#EF4444', glow: 'rgba(239,68,68,0.55)' },
  amber: { fill: '#F59E0B', glow: 'rgba(245,158,11,0.50)' },
  green: { fill: '#10B981', glow: 'rgba(16,185,129,0.35)' },
};

// Inject pulse keyframes once
if (typeof document !== 'undefined' && !document.getElementById('portfolio-map-pulse-css')) {
  const style = document.createElement('style');
  style.id = 'portfolio-map-pulse-css';
  style.innerHTML = `
    @keyframes portfolio-map-pulse {
      0%   { transform: scale(1);   opacity: 0.85; }
      70%  { transform: scale(2.2); opacity: 0;    }
      100% { transform: scale(2.2); opacity: 0;    }
    }
    .portfolio-map-marker-wrap {
      position: relative;
      display: flex; align-items: center; justify-content: center;
    }
    .portfolio-map-marker-pulse {
      position: absolute; inset: 0; border-radius: 999px;
      animation: portfolio-map-pulse 1.8s ease-out infinite;
    }
    .portfolio-map-legend { user-select: none; }
    .leaflet-container { font-family: inherit; }
  `;
  document.head.appendChild(style);
}

// Leaflet throws `Cannot read properties of undefined (reading '_leaflet_pos')`
// when invalidateSize() lands after the map panes have been torn down — i.e. a
// queued timer firing once the user has already navigated away. Guard on the
// live pane rather than swallowing the error after the fact.
const safeInvalidate = (map) => {
  try {
    if (map && map._mapPane && map._container && map._container.isConnected) {
      map.invalidateSize();
    }
  } catch { /* map already disposed */ }
};

// Auto-fit bounds to markers so all projects are visible in the tile
function FitToMarkers({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const valid = points.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (valid.length === 0) return;
    const bounds = valid.map(p => [p.lat, p.lng]);
    try {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 4 });
    } catch (e) { /* noop */ }
    const t = setTimeout(() => safeInvalidate(map), 200);
    return () => clearTimeout(t);
  }, [points, map]);
  return null;
}

// Handle window resize + parent flex resize
function AutoResize() {
  const map = useMap();
  useEffect(() => {
    const handler = () => safeInvalidate(map);
    window.addEventListener('resize', handler);
    const t1 = setTimeout(handler, 100);
    const t2 = setTimeout(handler, 600);
    return () => {
      window.removeEventListener('resize', handler);
      clearTimeout(t1); clearTimeout(t2);
    };
  }, [map]);
  return null;
}

// HTML-based pulsing marker with popup — used for every project
function PulseMarker({ point, isDark, onView }) {
  const map = useMap();
  const markerRef = useRef(null);
  useEffect(() => {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return undefined;
    const L = require('leaflet');
    const c = RAG_COLORS[point.rag_status] || RAG_COLORS.green;
    const pulse = point.rag_status === 'red' || point.rag_status === 'amber';
    const size = point.rag_status === 'red' ? 18 : point.rag_status === 'amber' ? 15 : 12;
    const html = `
      <div class="portfolio-map-marker-wrap" style="width:${size}px;height:${size}px">
        ${pulse ? `<div class="portfolio-map-marker-pulse" style="background:${c.glow}"></div>` : ''}
        <div style="
          position:relative;width:${size}px;height:${size}px;border-radius:999px;
          background:${c.fill};border:2px solid ${isDark ? '#0A0E1A' : '#ffffff'};
          box-shadow:0 2px 8px ${c.glow}, 0 0 0 1px ${c.fill}55;
        "></div>
      </div>`;
    const icon = L.divIcon({
      html,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    const marker = L.marker([point.lat, point.lng], { icon, riseOnHover: true });

    // Build popup HTML directly (using DOM to safely attach click handler on "View →")
    const spiC = point.spi >= 0.95 ? '#10B981' : point.spi >= 0.85 ? '#F59E0B' : '#EF4444';
    const cpiC = point.cpi >= 0.95 ? '#10B981' : point.cpi >= 0.85 ? '#F59E0B' : '#EF4444';
    const popupEl = document.createElement('div');
    popupEl.style.cssText = 'font-family:system-ui;min-width:240px';
    popupEl.innerHTML = `
      <div style="font-size:10px;color:#64748B;text-transform:uppercase;margin-bottom:4px;letter-spacing:0.6px">
        ${(point.sector || '—')} · <span style="color:${c.fill};font-weight:700">${point.risk_tier || 'LOW'}</span>
      </div>
      <div style="font-size:14px;font-weight:700;color:#0F172A;margin-bottom:8px">${point.name || ''}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:12px">
        <div><span style="color:#64748B">SPI:</span> <strong style="color:${spiC}">${Number(point.spi || 0).toFixed(2)}</strong></div>
        <div><span style="color:#64748B">CPI:</span> <strong style="color:${cpiC}">${Number(point.cpi || 0).toFixed(2)}</strong></div>
        <div><span style="color:#64748B">HI:</span> <strong>${point.hi_score || '—'}</strong></div>
        <div><span style="color:#64748B">BAC:</span> <strong>$${((point.bac || 0) / 1e9).toFixed(2)}B</strong></div>
      </div>
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee;display:flex;justify-content:space-between;align-items:center">
        <span style="color:#64748B;font-size:11px">${point.geography || ''}</span>
        <button data-role="view" data-testid="map-popup-view-${point.project_id || point.id}"
                style="background:#1E4E8C;color:#fff;border:0;border-radius:6px;padding:4px 8px;font-size:11px;cursor:pointer">View →</button>
      </div>`;
    popupEl.querySelector('[data-role="view"]').addEventListener('click', (e) => {
      e.stopPropagation();
      if (onView) onView(point);
    });
    marker.bindPopup(popupEl, { minWidth: 240, offset: [0, -size / 2] });

    marker.addTo(map);
    markerRef.current = marker;
    return () => {
      if (markerRef.current) {
        try { map.removeLayer(markerRef.current); } catch (e) { /* noop */ }
        markerRef.current = null;
      }
    };
  }, [point, isDark, map, onView]);
  return null;
}

export default function PortfolioMap({ projects, loading, isDark = true }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const points = useMemo(() => (projects || [])
    .map(p => ({
      ...p,
      lat: Number(p.lat),
      lng: Number(p.lng ?? p.lon),
    }))
    .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng)), [projects]);

  // Premium multi-layer basemap options — keyless CDNs (Carto @2x + ESRI)
  const basemaps = {
    smooth: isDark
      ? {
          name: 'Dark Matter',
          url: 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png',
        }
      : {
          name: 'Voyager',
          url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        },
    terrain: {
      name: 'Terrain',
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}',
    },
    satellite: {
      name: 'Satellite',
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    },
  };
  const labelsOverlay = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png';
  const attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

  if (loading) {
    return <div className="h-full w-full min-h-[400px] rounded-xl loading-shimmer" />;
  }

  const rc = points.reduce((acc, p) => { acc[p.rag_status || 'green'] = (acc[p.rag_status || 'green'] || 0) + 1; return acc; }, {});

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="relative h-full w-full"
      style={{ minHeight: 400 }}
      data-testid="portfolio-map-root"
    >
      <div
        className="absolute inset-0 rounded-xl overflow-hidden"
        style={{ border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.08)' }}
        data-testid="portfolio-map"
      >
        <MapContainer
          center={[20, 30]}
          zoom={2}
          minZoom={2}
          maxZoom={12}
          scrollWheelZoom={false}
          worldCopyJump
          style={{ height: '100%', width: '100%', background: isDark ? '#050914' : '#E6EEF7' }}
          attributionControl={false}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer name={basemaps.smooth.name}>
              <TileLayer url={basemaps.smooth.url} attribution={attribution} />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name={basemaps.terrain.name}>
              <TileLayer url={basemaps.terrain.url} attribution='Tiles &copy; Esri' />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer checked name={basemaps.satellite.name}>
              <TileLayer url={basemaps.satellite.url} attribution='Tiles &copy; Esri' />
            </LayersControl.BaseLayer>
            <LayersControl.Overlay name="Place labels">
              <TileLayer url={labelsOverlay} opacity={0.9} />
            </LayersControl.Overlay>
          </LayersControl>
          <AutoResize />
          <FitToMarkers points={points} />
          {points.map((p) => (
            <PulseMarker
              key={p.id || p.project_id}
              point={p}
              isDark={isDark}
              onView={(pt) => navigate(`/projects/${pt.project_id || pt.id}`)}
            />
          ))}
        </MapContainer>
      </div>

      {/* Legend with RAG counts */}
      <div
        className="portfolio-map-legend absolute bottom-3 left-3 flex items-center gap-3 px-3 py-1.5 rounded-md z-[400]"
        style={{
          background: isDark ? 'rgba(13,20,36,0.85)' : 'rgba(255,255,255,0.92)',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
          backdropFilter: 'blur(6px)',
        }}
        data-testid="map-legend"
      >
        <span className="text-[10px] uppercase tracking-wider" style={{ color: isDark ? '#94A3B8' : '#64748B' }}>RAG</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RAG_COLORS.red.fill, boxShadow: `0 0 6px ${RAG_COLORS.red.glow}` }} /><span className="text-[10px]" style={{ color: isDark ? '#CBD5E1' : '#334155' }}>Red · {rc.red || 0}</span></span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RAG_COLORS.amber.fill, boxShadow: `0 0 6px ${RAG_COLORS.amber.glow}` }} /><span className="text-[10px]" style={{ color: isDark ? '#CBD5E1' : '#334155' }}>Amber · {rc.amber || 0}</span></span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RAG_COLORS.green.fill }} /><span className="text-[10px]" style={{ color: isDark ? '#CBD5E1' : '#334155' }}>Green · {rc.green || 0}</span></span>
        </div>
      </div>

      {/* Project count badge */}
      <div
        className="absolute top-3 right-3 px-3 py-1.5 rounded-md z-[400]"
        style={{
          background: isDark ? 'rgba(13,20,36,0.85)' : 'rgba(255,255,255,0.92)',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <span className="text-xs" style={{ color: isDark ? '#94A3B8' : '#64748B' }}>
          <span className="font-semibold" style={{ color: isDark ? '#F8FAFC' : '#0F172A' }}>{points.length}</span> projects on map
        </span>
      </div>
    </motion.div>
  );
}
